//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Input, notification } from '@gamesb42/ui-kit';
import { useParams } from 'react-router-dom';
import baseRequest from 'queries/baseRequest';

import s from './fillQuestionnaireModal.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormValues {
  distribution: {
    appleStore: boolean;
    appleStoreComment: string;
    googleStore: boolean;
    googleStoreComment: string;
    steam: boolean;
    steamComment: string;
    other: boolean;
    otherComment: string;
  };
  monetization: string[];
  otherMonetizationComment: string;
  uaSpend: {
    facebook: boolean;
    googleAds: boolean;
    appleSearchAds: boolean;
    snapchat: boolean;
    tikTok: boolean;
    unityAds: boolean;
    dontKnow: boolean;
    other: boolean;
    otherComment: string;
  };
  mmp: {
    adjust: boolean;
    appsflyer: boolean;
    tenjin: boolean;
    kochava: boolean;
    branch: boolean;
    firebase: boolean;
    dontKnow: boolean;
    other: boolean;
    otherComment: string;
  };
  additionalInfo: string;
}

const INITIAL_VALUES: FormValues = {
  distribution: {
    appleStore: false,
    appleStoreComment: '',
    googleStore: false,
    googleStoreComment: '',
    steam: false,
    steamComment: '',
    other: false,
    otherComment: '',
  },
  monetization: [],
  otherMonetizationComment: '',
  uaSpend: {
    facebook: false,
    googleAds: false,
    appleSearchAds: false,
    snapchat: false,
    tikTok: false,
    unityAds: false,
    dontKnow: false,
    other: false,
    otherComment: '',
  },
  mmp: {
    adjust: false,
    appsflyer: false,
    tenjin: false,
    kochava: false,
    branch: false,
    firebase: false,
    dontKnow: false,
    other: false,
    otherComment: '',
  },
  additionalInfo: '',
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
  { key: 'distribution', label: 'Distribution', step: 1 },
  { key: 'monetization', label: 'Monetization', step: 2 },
  { key: 'uaSpends', label: 'UA spends sources', step: 3 },
  { key: 'mmp', label: 'Current MMP', step: 4 },
  { key: 'additionalInfo', label: 'Additional info', step: 5 },
];

const STEP_TITLES: Record<string, string> = {
  distribution: 'Where is the app or game distributed now?',
  monetization: 'Monetization model of your app/game?',
  uaSpends: 'UA spends sources:',
  mmp: 'Your current MMP (tracking service):',
  additionalInfo: 'Additional info about the project:',
};

const MONETIZATION_OPTIONS = [
  'In-app purchase',
  'Ads',
  'In-app purchase & Ads',
  'Premium',
  'Subscription',
  "I don't know",
  'Other',
];

const UA_SPEND_OPTIONS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'googleAds', label: 'Google Ads' },
  { key: 'appleSearchAds', label: 'Apple Search Ads' },
  { key: 'snapchat', label: 'Snapchat' },
  { key: 'tikTok', label: 'Tik Tok' },
  { key: 'unityAds', label: 'UnityAds' },
  { key: 'dontKnow', label: "I don't know" },
  { key: 'other', label: 'Other' },
];

const MMP_OPTIONS = [
  { key: 'adjust', label: 'Adjust' },
  { key: 'appsflyer', label: 'Appsflyer' },
  { key: 'tenjin', label: 'Tenjin' },
  { key: 'kochava', label: 'Kochava' },
  { key: 'branch', label: 'Branch' },
  { key: 'firebase', label: 'Firebase' },
  { key: 'dontKnow', label: "I don't know" },
  { key: 'other', label: 'Other' },
];

// ─── Step components ─────────────────────────────────────────────────────────

function StepDistribution({ values, onChange }: { values: FormValues; onChange: (v: FormValues) => void }) {
  const dist = values.distribution;

  const setDist = (patch: Partial<FormValues['distribution']>) =>
    onChange({ ...values, distribution: { ...dist, ...patch } });

  const platforms = [
    {
      key: 'appleStore',
      commentKey: 'appleStoreComment',
      label: 'Apple App Store',
      linkLabel: 'Link to your game/app at Apple App Store*',
    },
    {
      key: 'googleStore',
      commentKey: 'googleStoreComment',
      label: 'Google Play',
      linkLabel: 'Link to your game/app at Google Play*',
    },
    {
      key: 'steam',
      commentKey: 'steamComment',
      label: 'Steam',
      linkLabel: 'Link to your game/app at Steam*',
    },
    {
      key: 'other',
      commentKey: 'otherComment',
      label: 'Other',
      linkLabel: 'Enter your distribution platform',
    },
  ];

  return (
    <div className={s.stepContent}>
      {platforms.map(({ key, commentKey, label, linkLabel }) => (
        <div key={key} className={s.platformRow}>
          <Checkbox
            checked={dist[key]}
            onChange={(e) => setDist({ [key]: e.target.checked })}
          >
            {label}
          </Checkbox>

          {dist[key] && (
            <div className={s.platformInput}>
              <Input
                size="large"
                label={linkLabel}
                value={dist[commentKey]}
                onChange={(e) => setDist({ [commentKey]: e.target.value })}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StepMonetization({ values, onChange }: { values: FormValues; onChange: (v: FormValues) => void }) {
  const toggle = (option: string, checked: boolean) => {
    const next = checked
      ? [...values.monetization, option]
      : values.monetization.filter((o) => o !== option);
    onChange({
      ...values,
      monetization: next,
      otherMonetizationComment: !checked && option === 'Other' ? '' : values.otherMonetizationComment,
    });
  };

  return (
    <div className={s.stepContent}>
      <div className={s.checkboxList}>
        {MONETIZATION_OPTIONS.map((option) => (
          <Checkbox
            key={option}
            checked={values.monetization.includes(option)}
            onChange={(e) => toggle(option, e.target.checked)}
          >
            {option}
          </Checkbox>
        ))}
      </div>

      {values.monetization.includes('Other') && (
        <div className={s.otherInput}>
          <Input
            size="large"
            label="Enter your monetization model"
            value={values.otherMonetizationComment}
            onChange={(e) => onChange({ ...values, otherMonetizationComment: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

function StepUaSpend({ values, onChange }: { values: FormValues; onChange: (v: FormValues) => void }) {
  const toggle = (key: string, checked: boolean) => {
    onChange({ ...values, uaSpend: { ...values.uaSpend, [key]: checked } });
  };

  return (
    <div className={s.stepContent}>
      <div className={s.checkboxList}>
        {UA_SPEND_OPTIONS.map(({ key, label }) => (
          <Checkbox
            key={key}
            checked={Boolean(values.uaSpend[key])}
            onChange={(e) => toggle(key, e.target.checked)}
          >
            {label}
          </Checkbox>
        ))}
      </div>

      {values.uaSpend.other && (
        <div className={s.otherInput}>
          <Input
            size="large"
            label="Enter your UA spends sources"
            value={values.uaSpend.otherComment}
            onChange={(e) =>
              onChange({ ...values, uaSpend: { ...values.uaSpend, otherComment: e.target.value } })
            }
          />
        </div>
      )}
    </div>
  );
}

function StepMmp({ values, onChange }: { values: FormValues; onChange: (v: FormValues) => void }) {
  const toggle = (key: string, checked: boolean) => {
    onChange({ ...values, mmp: { ...values.mmp, [key]: checked } });
  };

  return (
    <div className={s.stepContent}>
      <div className={s.checkboxList}>
        {MMP_OPTIONS.map(({ key, label }) => (
          <Checkbox
            key={key}
            checked={Boolean(values.mmp[key])}
            onChange={(e) => toggle(key, e.target.checked)}
          >
            {label}
          </Checkbox>
        ))}
      </div>

      {values.mmp.other && (
        <div className={s.otherInput}>
          <Input
            size="large"
            label="Enter your MMP (tracking service)"
            value={values.mmp.otherComment}
            onChange={(e) =>
              onChange({ ...values, mmp: { ...values.mmp, otherComment: e.target.value } })
            }
          />
        </div>
      )}
    </div>
  );
}

function StepAdditionalInfo({ values, onChange }: { values: FormValues; onChange: (v: FormValues) => void }) {
  return (
    <div className={s.stepContent}>
      <Input.TextArea
        style={{ height: '140px' }}
        label="Comment"
        showCount
        maxLength={400}
        value={values.additionalInfo}
        onChange={(e) => onChange({ ...values, additionalInfo: e.target.value })}
      />
    </div>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────

function isStepValid(tabKey: string, values: FormValues): boolean {
  switch (tabKey) {
    case 'distribution': {
      const d = values.distribution;
      return (
        (d.appleStore && d.appleStoreComment.trim().length > 0) ||
        (d.googleStore && d.googleStoreComment.trim().length > 0) ||
        (d.steam && d.steamComment.trim().length > 0) ||
        (d.other && d.otherComment.trim().length > 0)
      );
    }
    case 'monetization':
      return values.monetization.length > 0;
    case 'uaSpends': {
      const { otherComment, ...booleans } = values.uaSpend;
      return Object.values(booleans).some(Boolean);
    }
    case 'mmp': {
      const { otherComment, ...booleans } = values.mmp;
      return Object.values(booleans).some(Boolean);
    }
    case 'additionalInfo':
      return true;
    default:
      return false;
  }
}

// ─── PATCH /v1/onboarding/{company_id} → AppQuestionaryEditDTO ───────────────

function buildPayload(values: FormValues) {
  // monetization: "Other" заменяется на "Other: <comment>"
  const monetization = values.monetization.map((item) =>
    item === 'Other' && values.otherMonetizationComment.trim()
      ? `Other: ${values.otherMonetizationComment.trim()}`
      : item,
  );

  // uaSpend / mmp: otherComment остаётся внутри объекта как есть (ожидается бэком)
  const { otherComment: uaOtherComment, ...uaSpendBooleans } = values.uaSpend;
  const uaSpend = values.uaSpend.other
    ? values.uaSpend
    : { ...uaSpendBooleans, otherComment: '' };

  const { otherComment: mmpOtherComment, ...mmpBooleans } = values.mmp;
  const mmp = values.mmp.other
    ? values.mmp
    : { ...mmpBooleans, otherComment: '' };

  return {
    distribution: values.distribution,
    monetization,
    uaSpend,
    mmp,
    additionalInfo: values.additionalInfo,
  };
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface FillQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: any;
}

function buildInitialValues(data?: any): FormValues {
  if (!data?.app_questionary) return INITIAL_VALUES;

  const q = data.app_questionary;

  // monetization: "Other: comment" → Other=true + otherMonetizationComment
  const rawMonetization: string[] = Array.isArray(q.monetization) ? q.monetization : [];
  const otherMonetizationItem = rawMonetization.find((m) => m.startsWith('Other:'));
  const monetization = rawMonetization.map((m) => (m.startsWith('Other:') ? 'Other' : m));
  const otherMonetizationComment = otherMonetizationItem
    ? otherMonetizationItem.replace('Other:', '').trim()
    : '';

  return {
    distribution: { ...INITIAL_VALUES.distribution, ...(q.distribution || {}) },
    monetization,
    otherMonetizationComment,
    uaSpend: { ...INITIAL_VALUES.uaSpend, ...(q.uaSpend || {}) },
    mmp: { ...INITIAL_VALUES.mmp, ...(q.mmp || {}) },
    additionalInfo: q.additionalInfo || '',
  };
}

export function FillQuestionnaireModal({ isOpen, onClose, onSuccess, initialData }: FillQuestionnaireModalProps) {
  const [activeTab, setActiveTab] = useState('distribution');
  const [values, setValues] = useState<FormValues>(() => buildInitialValues(initialData));
  const [loading, setLoading] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();

  const currentIndex = TABS.findIndex((t) => t.key === activeTab);
  const currentTab = TABS[currentIndex];
  const isLast = currentIndex === TABS.length - 1;
  const canProceed = isStepValid(activeTab, values);

  const hasDontKnow = (tabKey: string): boolean => {
    switch (tabKey) {
      case 'monetization':
        return values.monetization.includes("I don't know");
      case 'uaSpends':
        return values.uaSpend.dontKnow;
      case 'mmp':
        return values.mmp.dontKnow;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setActiveTab('distribution');
      setValues(buildInitialValues(initialData));
    }
  }, [isOpen, initialData]);

  const submitData = async () => {
    setLoading(true);
    const sca = (window as any).ENV?.REACT_APP_SCA_URL || process.env.REACT_APP_SCA_URL;

    try {
      // PATCH /v1/onboarding/{company_id} — AppQuestionaryEditDTO
      await baseRequest({
        path: `${sca}/v1/onboarding/${companyId}`,
        options: {
          method: 'PATCH',
          body: JSON.stringify(buildPayload(values)),
        },
      });

      notification.success({
        message: 'Questionnaire has been submitted successfully',
        theme: 'dark',
        placement: 'bottomLeft',
      });

      onSuccess?.();
      onClose();
    } catch {
      notification.error({
        message: 'Failed to submit questionnaire. Please try again.',
        theme: 'dark',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    if (!isLast && !initialData?.app_questionary) {
      setActiveTab(TABS[currentIndex + 1].key);
      return;
    }

    if (initialData?.app_questionary) {
      onClose();
      Modal.warning({
        title: 'Confirm changes',
        okButtonProps: { style: { background: '#EC3E72', border: 'none' } },
        okText: 'Save changes',
        content: 'Changing this form may delete previously collected user data.',
        onOk: submitData,
        onCancel: () => {},
      });
    } else {
      submitData();
    }
  };

  const renderStep = () => {
    switch (activeTab) {
      case 'distribution':
        return <StepDistribution values={values} onChange={setValues} />;
      case 'monetization':
        return <StepMonetization values={values} onChange={setValues} />;
      case 'uaSpends':
        return <StepUaSpend values={values} onChange={setValues} />;
      case 'mmp':
        return <StepMmp values={values} onChange={setValues} />;
      case 'additionalInfo':
        return <StepAdditionalInfo values={values} onChange={setValues} />;
      default:
        return null;
    }
  };

  return (
    <>
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={initialData?.app_questionary ? STEP_TITLES[activeTab] : `Step ${currentTab.step}/${TABS.length}: ${STEP_TITLES[activeTab]}`}
      footer={null}
      width={768}
    >
      <div className={s.modalBody}>
        <div className={s.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''} ${hasDontKnow(tab.key) ? s.tabDontKnow : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={s.content}>{renderStep()}</div>

        {initialData?.app_questionary && (
          <div className={s.warningBanner}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={s.warningIcon}>
              <path fillRule="evenodd" clipRule="evenodd" d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21ZM12 7C12.5523 7 13 7.44772 13 8V13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V8C11 7.44772 11.4477 7 12 7ZM11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16Z" fill="#F57B51"/>
            </svg>
            Changing this form may delete previously collected user data.
          </div>
        )}

        <div className={s.footer}>
          <Button
            type="primary"
            onClick={handleProceed}
            disabled={loading}
            loading={loading}
          >
            {initialData?.app_questionary ? 'Save changes' : isLast ? 'Submit' : 'Proceed'}
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
}
